import type { Node, Edge, SocketDefinition } from '../types';
import { isMarkupType } from '../constants/nodeTypes';
import { getSocketOutwardDir, bezierControlOffset } from './edgeMath';

export interface AdjustedControlPoints {
  [edgeId: string]: { cp1: { x: number; y: number }; cp2: { x: number; y: number } };
}

interface Box { x: number; y: number; w: number; h: number }
interface Vec { x: number; y: number }

/** Keep the stroke this far outside a node body. */
const PAD = 10;
/** Hard cap from the natural bezier handle — prevents canvas-wide loops. */
export const FORCE_EDGE_MAX_SHIFT = 64;
const SAMPLE_TS = [0.25, 0.4, 0.5, 0.6, 0.75];

function isInsideBox(px: number, py: number, b: Box): boolean {
  return px > b.x && px < b.x + b.w && py > b.y && py < b.y + b.h;
}

function boxOf(node: Node, pad: number): Box {
  return {
    x: node.position.x - pad,
    y: node.position.y - pad,
    w: node.size.width + 2 * pad,
    h: node.size.height + 2 * pad,
  };
}

function boxesOverlap(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function aabb(points: Vec[]): Box {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function expand(b: Box, m: number): Box {
  return { x: b.x - m, y: b.y - m, w: b.w + 2 * m, h: b.h + 2 * m };
}

/** Immediately push `pt` to just outside the nearest edge of `box`. */
function pushOutOfBox(pt: Vec, box: Box): void {
  if (!isInsideBox(pt.x, pt.y, box)) return;
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const halfW = box.w / 2;
  const halfH = box.h / 2;
  const dx = pt.x - cx;
  const dy = pt.y - cy;
  const overlapX = halfW - Math.abs(dx);
  const overlapY = halfH - Math.abs(dy);
  if (overlapX < overlapY) {
    pt.x += Math.sign(dx || 1) * (overlapX + 1);
  } else {
    pt.y += Math.sign(dy || 1) * (overlapY + 1);
  }
}

function clampShift(pt: Vec, nat: Vec, max: number): void {
  const dx = pt.x - nat.x;
  const dy = pt.y - nat.y;
  const len = Math.hypot(dx, dy);
  if (len <= max || len === 0) return;
  const s = max / len;
  pt.x = nat.x + dx * s;
  pt.y = nat.y + dy * s;
}

function cubicPoint(p0: Vec, p1: Vec, p2: Vec, p3: Vec, t: number): Vec {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
}

function shortestEscape(sample: Vec, box: Box): Vec {
  const toLeft = sample.x - box.x;
  const toRight = box.x + box.w - sample.x;
  const toTop = sample.y - box.y;
  const toBot = box.y + box.h - sample.y;
  if (Math.min(toLeft, toRight) < Math.min(toTop, toBot)) {
    return { x: (toLeft < toRight ? -toLeft : toRight) + 1, y: 0 };
  }
  return { x: 0, y: (toTop < toBot ? -toTop : toBot) + 1 };
}

function isObstacle(node: Node, srcId: string, tgtId: string, p0: Vec, p3: Vec): boolean {
  if (node.id === srcId || node.id === tgtId) return false;
  if (node.type === 'group') return false;
  if (isMarkupType(node.type)) return false;
  const raw = boxOf(node, 0);
  // Parent / container the wire is meant to stay inside.
  if (isInsideBox(p0.x, p0.y, raw) || isInsideBox(p3.x, p3.y, raw)) return false;
  return true;
}

/**
 * Local vicinity nudge for bezier control points.
 *
 * Only nodes whose padded box overlaps the natural curve corridor are considered.
 * Displacement from the natural handles is capped so wires cannot wander across
 * the canvas the way a global box-repulsion loop does.
 */
export function computeForceAdjustedControlPoints(
  edges: Edge[],
  nodes: Node[],
  getSocket: (nodeId: string, socketId: string) => SocketDefinition | undefined,
  iterations = 4,
  laneShifts: Record<string, number> = {},
): AdjustedControlPoints {
  if (edges.length === 0 || nodes.length === 0) return {};

  const nodeById = new Map(nodes.map(n => [n.id, n]));
  const passes = Math.max(1, Math.min(iterations, 6));
  const result: AdjustedControlPoints = {};

  for (const edge of edges) {
    if (edge.routingMode === 'orthogonal') continue;
    if (edge.waypoints?.length) continue;

    const srcNode = nodeById.get(edge.sourceNodeId);
    const tgtNode = nodeById.get(edge.targetNodeId);
    if (!srcNode || !tgtNode) continue;

    const srcSock = getSocket(srcNode.id, edge.sourceSocketId);
    const tgtSock = getSocket(tgtNode.id, edge.targetSocketId);
    if (!srcSock || !tgtSock) continue;

    const p0 = {
      x: srcNode.position.x + srcSock.position.x,
      y: srcNode.position.y + srcSock.position.y,
    };
    const p3 = {
      x: tgtNode.position.x + tgtSock.position.x,
      y: tgtNode.position.y + tgtSock.position.y,
    };

    const srcExit = getSocketOutwardDir(srcSock, srcNode.size);
    const tgtExit = getSocketOutwardDir(tgtSock, tgtNode.size);
    const totalDist = Math.hypot(p3.x - p0.x, p3.y - p0.y);
    const curveStr = edge.curveStrength ?? 1.0;
    const controlOffset = bezierControlOffset(totalDist, curveStr);
    const laneShift = laneShifts[edge.id] ?? 0;
    const spreadX = -srcExit.dy * laneShift;
    const spreadY = srcExit.dx * laneShift;

    const nat1: Vec = {
      x: p0.x + srcExit.dx * controlOffset + spreadX,
      y: p0.y + srcExit.dy * controlOffset + spreadY,
    };
    const nat2: Vec = {
      x: p3.x + tgtExit.dx * controlOffset + spreadX,
      y: p3.y + tgtExit.dy * controlOffset + spreadY,
    };

    const corridor = expand(aabb([p0, nat1, nat2, p3]), PAD);
    const nearby = nodes.filter(n =>
      isObstacle(n, srcNode.id, tgtNode.id, p0, p3) && boxesOverlap(boxOf(n, PAD), corridor),
    );
    if (nearby.length === 0) continue;

    const hitsNatural = nearby.some(n => {
      const b = boxOf(n, PAD);
      if (isInsideBox(nat1.x, nat1.y, b) || isInsideBox(nat2.x, nat2.y, b)) return true;
      return SAMPLE_TS.some(t => {
        const s = cubicPoint(p0, nat1, nat2, p3, t);
        return isInsideBox(s.x, s.y, b);
      });
    });
    if (!hitsNatural) continue;

    const cp1: Vec = { ...nat1 };
    const cp2: Vec = { ...nat2 };

    for (let pass = 0; pass < passes; pass++) {
      for (const n of nearby) {
        const b = boxOf(n, PAD);
        pushOutOfBox(cp1, b);
        pushOutOfBox(cp2, b);
      }
      clampShift(cp1, nat1, FORCE_EDGE_MAX_SHIFT);
      clampShift(cp2, nat2, FORCE_EDGE_MAX_SHIFT);

      for (const t of SAMPLE_TS) {
        const sample = cubicPoint(p0, cp1, cp2, p3, t);
        for (const n of nearby) {
          const b = boxOf(n, PAD);
          if (!isInsideBox(sample.x, sample.y, b)) continue;
          const push = shortestEscape(sample, b);
          const w1 = (1 - t) * 0.55;
          const w2 = t * 0.55;
          cp1.x += push.x * w1;
          cp1.y += push.y * w1;
          cp2.x += push.x * w2;
          cp2.y += push.y * w2;
        }
      }
      clampShift(cp1, nat1, FORCE_EDGE_MAX_SHIFT);
      clampShift(cp2, nat2, FORCE_EDGE_MAX_SHIFT);
    }

    if (Math.hypot(cp1.x - nat1.x, cp1.y - nat1.y) < 0.5
      && Math.hypot(cp2.x - nat2.x, cp2.y - nat2.y) < 0.5) {
      continue;
    }

    result[edge.id] = { cp1, cp2 };
  }

  return result;
}
